import { computed, ref } from 'vue';
import { createSharedComposable, reactify, useStorage } from '@vueuse/core';
import { useQueryClient } from '@tanstack/vue-query';
import { api } from '/app/plugins/services';
import { useToast } from '/app/composables/toast';
import { track } from '/app/utils/analytics';
import { createEtaEstimator } from '/app/utils/eta';

// Persisted, cross-tab registry of video uploads whose download is still being
// enabled server-side. The `ensureVideoReady` poll can take minutes-to-hours, so
// we keep a record in localStorage and resume the poll on the next page load.
//
// Entry shape: { key /* = videoId */, label, type, status, reason? }
//   status: PROCESSING (poll running) | FAILED (timed out / terminal; awaits Retry)
//   (a ready video is removed from the registry entirely)
const STORAGE_KEY = 'uploads:video-processing';
const MAX_ENTRIES = 50;

export const VIDEO_STATUS = {
    PROCESSING: 'processing',
    FAILED: 'failed',
};

export const useVideoProcessing = createSharedComposable(() => {
    // useStorage keeps this reactive AND synced across tabs via storage events,
    // so a badge in any tab reflects the registry live.
    const entries = useStorage(STORAGE_KEY, [], undefined, { writeDefaults: false });
    const toast = useToast();

    // queryClient is only needed to refresh lists after a video goes ready. The
    // badge itself is driven by the registry, so invalidation is best-effort:
    // if there's no inject context (first call outside setup), skip it.
    let queryClient = null;
    try {
        queryClient = useQueryClient();
    } catch {
        queryClient = null;
    }

    // Per-tab single-flight: never poll the same videoId twice in THIS tab.
    // Cross-tab duplicates are accepted by design — the PATCH must be idempotent.
    const inFlight = new Set();

    const find = (key) => entries.value.find((entry) => entry.key === key);
    // reactify => pass a key (ref or plain) and get a ComputedRef back that
    // re-evaluates when the key OR the registry changes. Call sites read .value.
    const isProcessing = reactify((key) => find(key)?.status === VIDEO_STATUS.PROCESSING);
    const isFailed = reactify((key) => find(key)?.status === VIDEO_STATUS.FAILED);
    const statusOf = reactify((key) => find(key)?.status ?? null);

    // Per-tab step + remaining-time for the tooltip. Estimators hold the sample
    // state per key; etaState is the reactive slice the tooltip reads.
    const ETA_STEPS = new Set(['video-processing', 'download-processing']);
    const estimators = new Map(); // key -> { step, estimator }
    const etaState = ref({});     // key -> { step, etaMs }

    const trackProgress = (key, { step, progress } = {}) => {
        if (typeof progress !== 'number' || !ETA_STEPS.has(step)) return;

        let entry = estimators.get(key);
        if (!entry || entry.step !== step) {
            // Each step is its own 0→100 scale, so start a fresh estimator.
            entry = { step, estimator: createEtaEstimator() };
            estimators.set(key, entry);
        }

        const etaMs = entry.estimator.push(progress);
        etaState.value = { ...etaState.value, [key]: { step, etaMs } };
    };

    const clearEta = (key) => {
        estimators.delete(key);
        if (!etaState.value[key]) return;
        const next = { ...etaState.value };
        delete next[key];
        etaState.value = next;
    };

    const progressInfoOf = reactify((key) => etaState.value[key] ?? null);

    const upsert = (entry) => {
        const next = entries.value.filter((item) => item.key !== entry.key);
        next.unshift(entry);
        entries.value = next.slice(0, MAX_ENTRIES);
    };

    const remove = (key) => {
        entries.value = entries.value.filter((entry) => entry.key !== key);
        clearEta(key);
    };

    const setStatus = (key, status, patch = {}) => {
        entries.value = entries.value.map((entry) =>
            entry.key === key ? { ...entry, ...patch, status } : entry,
        );
    };

    // Prefix-match invalidation covers every platform's list without threading a
    // platformId through the registry.
    const refreshLists = () => {
        queryClient?.invalidateQueries({ queryKey: ['storage-library'] });
        queryClient?.invalidateQueries({ queryKey: ['storage-library-flat'] });
    };

    // Drive the readiness poll in this shared scope so it outlives the upload
    // component — closing the modal or navigating away must not abort it.
    const poll = async (entry) => {
        const { key } = entry;

        if (inFlight.has(key)) return;
        inFlight.add(key);

        try {
            await api.forum.ensureVideoReady(key, { op: 'enable-download' }, {
                onUpdateProgress: (update) => trackProgress(key, update),
            });

            // Download enabled: drop the entry (badge disappears) and refresh
            // lists so the card re-renders with its thumbnail.
            remove(key);
            refreshLists();

            track('Video Download Enabled', { videoId: key, label: entry.label, type: entry.type });
        } catch (err) {
            // Timeout (VIDEO_NOT_READY) or terminal failure: keep the entry as
            // FAILED so the badge persists with a Retry. resumeAll() never
            // auto-restarts FAILED entries, so a broken video can't re-poll
            // forever across reloads.
            const reason = err?.code || err?.message;

            setStatus(key, VIDEO_STATUS.FAILED, { reason: reason || 'unknown' });
            toast.error(`Video "${entry.label || key}" couldn't be prepared for download.`);

            track('Video Download Failed', { videoId: key, label: entry.label, type: entry.type, reason });
        } finally {
            inFlight.delete(key);
        }
    };

    // Register a freshly uploaded video and start polling immediately.
    const register = (entry) => {
        if (!entry?.key) return null;

        const record = {
            key: entry.key,
            label: entry.label ?? null,
            type: entry.type ?? null,
            status: VIDEO_STATUS.PROCESSING,
        };

        upsert(record);
        poll(record);

        return record;
    };

    // Re-run a failed entry on user request.
    const retry = (key) => {
        const entry = find(key);
        if (!entry) return;

        clearEta(key);
        setStatus(key, VIDEO_STATUS.PROCESSING, { reason: undefined });
        poll({ ...entry, status: VIDEO_STATUS.PROCESSING });
    };

    // Called once on app boot: resume polling for still-processing entries only.
    const resumeAll = () => {
        for (const entry of entries.value) {
            if (entry.status === VIDEO_STATUS.PROCESSING) {
                poll(entry);
            }
        }
    };

    return {
        entries: computed(() => entries.value),
        isProcessing,
        isFailed,
        statusOf,
        progressInfoOf,
        register,
        remove,
        retry,
        resumeAll,
    };
});
